// Letters and mailboxes of the store for the mail client (R-M6): re-read when a letter or a mailbox changes.

import { useMemo } from 'react';
import type { Id, RecordObj } from '@confdeck/core-shared';
import { useMeta, useSync, useTypesTick } from '../../sync/react.tsx';
import { letterOf, type Letter } from './model.ts';

export interface Mailbox { id: Id; name: string; address: string; kind: string; syncState: string; syncError: string; lastSync: string; signature: string; rec: RecordObj }

export function useMailTypes() {
  const meta = useMeta();
  return { letterType: meta.typeByKey('mail_letter'), mailboxType: meta.typeByKey('mailbox') };
}

export function useMailboxes(): Mailbox[] {
  const client = useSync();
  const { mailboxType } = useMailTypes();
  const tick = useTypesTick(mailboxType ? [mailboxType.id] : []);
  return useMemo(() => {
    if (!mailboxType) return [];
    const out: Mailbox[] = [];
    for (const id of client.store.recordIds(mailboxType.id)) {
      const r = client.store.record(id);
      if (!r || r.archivedAt) continue;
      const d = r.data;
      out.push({ id, rec: r, name: String(d.name ?? d.address ?? ''), address: String(d.address ?? ''), kind: String(d.kind ?? ''), syncState: String(d.sync_state ?? ''), syncError: String(d.sync_error ?? ''), lastSync: String(d.last_sync ?? ''), signature: String(d.signature ?? '') });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [client, mailboxType, tick]);
}

/** Every letter the person sees, newest first. */
export function useLetters(): Letter[] {
  const client = useSync();
  const { letterType } = useMailTypes();
  const tick = useTypesTick(letterType ? [letterType.id] : []);
  return useMemo(() => {
    if (!letterType) return [];
    const box = client.store.meta.field(letterType.id, 'mailbox');
    const out: Letter[] = [];
    for (const id of client.store.recordIds(letterType.id)) {
      const r = client.store.record(id);
      if (!r || r.archivedAt) continue;
      out.push(letterOf(r, box ? client.store.linked(r, box)[0] ?? null : null));
    }
    return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [client, letterType, tick]);
}
